/**
 * Feature flags de la capa offline.
 *
 * `offline_mode_enabled` decide si TAREAS captura offline (crear, editar, borrar y evidencias desde el
 * menú del ítem, catálogos de tareas y la tarea automática). Formularios y el arranque de la base no la
 * leen: trabajan siempre sobre la base local. El worker de sync solo la usa para frenar el pull de
 * catálogos (los usa Tareas); el drenado de lo capturado nunca depende de ella. Si está en false, Tareas
 * va en línea.
 *
 * DEFAULT = true. Se apaga o se acota SIN tocar código, por
 * PRECEDENCIA (gana el primero que aplique):
 *
 *   1) PER-DEVICE — override de LocalStorage. Manda SIEMPRE, en ambas direcciones (QA/piloto/smokes):
 *      setFlagOverride('offline_mode_enabled', false) — o
 *      localStorage.setItem('offline_flag_overrides', '{"offline_mode_enabled":false}').
 *   2) KILL SWITCH GLOBAL — env de build `OFFLINE_MODE_ENABLED`. Si viene explícita, MANDA sobre el
 *      default y sobre la allowlist: 'false'/'0'/'off'/'no' apagan; 'true'/'1'/'on'/'yes' encienden.
 *      Ausente = no opina (sigue la cadena). Es la palanca para apagar Tareas offline en un deploy sin
 *      release de código.
 *   3) PILOTO POR ORGANIZACIÓN — env de build `OFFLINE_ENABLED_ORGS` (ids separados por coma). Si trae
 *      algo, ACOTA: solo esas orgs capturan Tareas offline; el resto queda OFF aunque el default sea true.
 *      Vacía/ausente = no acota. El org id se resuelve en runtime via un resolver INYECTADO en boot
 *      (boot/offline.js llama setOrgResolver) — flags.js NO importa stores, para no crear ciclos.
 *   4) DEFAULT — true.
 *
 * ALCANCE REAL DE LAS ENVS (importante, no es un detalle): se inlinean en tiempo de BUILD (DefinePlugin
 * de quasar.config.js), y `scripts/build-android.sh` las lee del `.env` del repo. O sea: en el deploy WEB
 * cambiar la env + redeploy apaga Tareas offline SIN tocar la app; en la APK de Android/iOS, cambiarla exige
 * GENERAR UNA APK NUEVA. Para apagar un móvil ya instalado sin release, la única vía es el override
 * per-device (1) o un kill switch SERVER-SIDE, que sigue pendiente y se trackea aparte.
 */

const DEFAULTS = {
  offline_mode_enabled: true, // ON por defecto; se apaga/acota por env (ver la precedencia de arriba)
  // Web-only: gatea el Web Lock de selectBackend.js que evita que opfs-sahpool truene con
  // RuntimeError: unreachable cuando hay más de una pestaña/dispositivo abiertos con la misma
  // cuenta (solo la primera pestaña usa opfs-sahpool; el resto cae a wa-sqlite-idb). Kill
  // switch de emergencia sin release: setFlagOverride('offline_multitab_guard', false).
  offline_multitab_guard: true
}

/**
 * Lee una env booleana de build. Devuelve undefined si no viene (para que "ausente" NO signifique false
 * y la cadena de precedencia pueda seguir). Se lee `import.meta.env.X` (mecanismo nativo de Vite,
 * siempre definido — a diferencia de `process.env.X`, no requiere define/DefinePlugin y funciona igual
 * en dev y build).
 */
const envBool = (raw) => {
  if (raw == null) return undefined
  const v = String(raw).trim().toLowerCase()
  if (!v) return undefined
  if (['false', '0', 'off', 'no'].includes(v)) return false
  if (['true', '1', 'on', 'yes'].includes(v)) return true
  return undefined // valor no reconocido: no opina (mejor que apagar la capa por un typo)
}

/** Kill switch global por env de build. undefined = ausente = no opina. */
const offlineModeEnv = () => {
  let raw
  try { raw = import.meta.env.OFFLINE_MODE_ENABLED } catch { raw = undefined }
  return envBool(raw)
}

/** Último valor conocido del kill switch remoto. undefined = nunca se supo = no opina. */
const remoteOfflineFlag = () => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(REMOTE_FLAG_KEY) : null
    if (raw === 'true') return true
    if (raw === 'false') return false
    return undefined
  } catch {
    return undefined
  }
}

const OVERRIDE_KEY = 'offline_flag_overrides'

/**
 * Cache del KILL SWITCH REMOTO (lo escribe remote-flag.js; aquí solo se LEE).
 *
 * Vive en localStorage y NO en SQLite a propósito: se lee de forma SÍNCRONA, sin red y sin depender de la
 * BD. El valor cacheado es la última respuesta del server; en un arranque sin red se usa esa (es justo lo que lo hace útil: si
 * apagas Tareas offline desde el back, el device lo respeta aunque luego se quede sin señal).
 *
 * flags.js NO importa nada para leerlo (localStorage pelón) — así no hay ciclos y el fetcher, que sí
 * importa servicios, puede depender de este módulo en un solo sentido.
 */
export const REMOTE_FLAG_KEY = 'offline_remote_flag'

// Allowlist de orgs del piloto offline-v2, desde la env de build `OFFLINE_ENABLED_ORGS` (ids de
// organization separados por coma). Vacía = no acota. El build del piloto la setea; producción la deja
// ausente. Se comparan como STRING (number|string).
// Se lee `import.meta.env.OFFLINE_ENABLED_ORGS` (mecanismo nativo de Vite, siempre definido en dev y
// build); en node (tests) lo resuelve/stubbea vi.stubEnv. Call-time.
const offlineOrgAllowlist = () => {
  let raw = ''
  try { raw = import.meta.env.OFFLINE_ENABLED_ORGS || '' } catch { raw = '' }
  return new Set(String(raw).split(',').map(s => s.trim()).filter(Boolean))
}

// Resolver del org id del usuario autenticado, INYECTADO en boot (mismo patrón que setScopeResolver de
// boot/offline.js). flags.js NO importa el store -> evita imports circulares (stores/auth, stores/
// occurrences y sync-worker importan flags). Devuelve undefined si no hay usuario/resolver.
let _orgResolver = null
export const setOrgResolver = fn => { _orgResolver = fn }
const currentOrgId = () => {
  try { return _orgResolver ? _orgResolver() : undefined } catch { return undefined }
}

// localStorage NATIVO (no el de Quasar): así el override seteado por consola
// (localStorage.setItem(OVERRIDE_KEY, JSON.stringify({...}))) se lee idéntico,
// sin depender de la codificación propia de Quasar.
const readOverrides = () => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(OVERRIDE_KEY) : null
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

/** Devuelve el set completo de flags (defaults + overrides locales). */
export const getFlags = () => ({ ...DEFAULTS, ...readOverrides() })

/**
 * Resuelve la bandera `offline_mode_enabled` por la PRECEDENCIA documentada arriba:
 * override per-device > env OFFLINE_MODE_ENABLED > allowlist de orgs (si acota) > default (true).
 */
const resolveOfflineModeEnabled = () => {
  // 1) per-device: manda siempre, en ambas direcciones.
  const overrides = readOverrides()
  if (Object.prototype.hasOwnProperty.call(overrides, 'offline_mode_enabled')) {
    return !!overrides.offline_mode_enabled
  }
  // 2) kill switch global por env: si viene explícita, decide (apaga o enciende) y corta la cadena.
  const envValue = offlineModeEnv()
  if (envValue !== undefined) return envValue
  // 3) kill switch REMOTO (server): el apagado de emergencia sin release. Es el último valor que el
  //    server dijo; si nunca se supo, no opina.
  const remoteValue = remoteOfflineFlag()
  if (remoteValue !== undefined) return remoteValue
  // 4) piloto: si hay allowlist, ACOTA a esas orgs. Vacía = no acota.
  const allowlist = offlineOrgAllowlist()
  if (allowlist.size) {
    const orgId = currentOrgId()
    return orgId != null && allowlist.has(String(orgId))
  }
  // 4) default.
  return DEFAULTS.offline_mode_enabled
}

/**
 * True solo si la bandera global está activa Y el flag pedido está activo.
 * `offline_mode_enabled` se evalúa por sí mismo (override local u org allowlist).
 */
export const isFlagEnabled = name => {
  const globalOn = resolveOfflineModeEnabled()
  if (name === 'offline_mode_enabled') return globalOn
  return globalOn && !!getFlags()[name]
}

/** Override local de un flag (debug/piloto). Persiste en localStorage nativo. */
export const setFlagOverride = (name, value) => {
  const overrides = readOverrides()
  overrides[name] = value
  try {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(overrides))
  } catch {
    // sin localStorage: no-op
  }
}

/** Limpia todos los overrides locales (vuelve a defaults). */
export const clearFlagOverrides = () => {
  try {
    localStorage.removeItem(OVERRIDE_KEY)
  } catch {
    // no-op
  }
}
